import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PHOTOVOLTAIC, REALESTATE, EMOBILITY } from "../data/data";
import HtmlContent from "./HtmlContent";
import { mediaUrl, sectionBlocks, useSiteData } from "../lib/siteApi";

const CATEGORY_META = {
  photovoltaic: {
    title: "Photovoltaic Solar Energy",
    description: "Photovoltaic solar energy is a clean and renewable energy source that harnesses solar radiation to produce electricity. With proper installation, solar energy offers cost-free electricity for up to 25 years through a one-time investment.",
    color: "teal",
    icon: "☀️",
  },
  realestate: {
    title: "Smart & Green Real Estate",
    description: "Traditional houses are evolving into modern, internet-connected buildings that prioritize safety, convenience, and cost efficiency. Transitioning to green energy is essential.",
    color: "green",
    icon: "🏢",
  },
  emobility: {
    title: "E-Mobility Services",
    description: "E-vehicles offer a cleaner alternative to combustion vehicles, reducing atmospheric pollution and supporting Rwanda's sustainable transport future.",
    color: "blue",
    icon: "⚡",
  },
};

const COLOR_CYCLE = ["teal", "green", "blue"];

const fallbackData = {
  photovoltaic: { ...CATEGORY_META.photovoltaic, services: PHOTOVOLTAIC },
  realestate: { ...CATEGORY_META.realestate, services: REALESTATE },
  emobility: { ...CATEGORY_META.emobility, services: EMOBILITY },
};

function prettyCategory(slug) {
  if (CATEGORY_META[slug]) return CATEGORY_META[slug].title;
  return slug.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function groupServices(items) {
  const grouped = {};
  items.forEach((item, index) => {
    const key = (item.category || "general").toLowerCase();
    if (!grouped[key]) {
      const meta = CATEGORY_META[key] || {
        title: prettyCategory(key),
        description: `RENERG ${prettyCategory(key)} solutions for homes, businesses, and communities in Rwanda.`,
        color: COLOR_CYCLE[Object.keys(grouped).length % COLOR_CYCLE.length],
        icon: item.icon || "☀️",
      };
      grouped[key] = { ...meta, services: [] };
    }
    grouped[key].services.push({
      title: item.title,
      desc: item.description,
      icon: item.icon || grouped[key].icon,
      image: mediaUrl(item.imageUrl),
      key: item.id || `${key}-${index}`,
      id: item.id,
    });
  });
  return grouped;
}

export default function Services() {
    const site = useSiteData();
    const [activeTab, setActiveTab] = useState("photovoltaic");
    const [servicesData, setServicesData] = useState(fallbackData);
    const intro = sectionBlocks(site.content, "services")[0];

    useEffect(() => {
        if (!site.services?.length) return;
        const grouped = groupServices(site.services);
        setServicesData(grouped);
        setActiveTab((current) => (grouped[current] ? current : Object.keys(grouped)[0]));
    }, [site.services]);

    const colorClasses = {
        teal: {
            bg: "bg-teal-50 dark:bg-teal-900/20",
            text: "text-teal-600 dark:text-teal-400",
            active: "bg-teal-600 text-white"
        },
        green: {
            bg: "bg-green-50 dark:bg-green-900/20",
            text: "text-green-600 dark:text-green-400",
            active: "bg-green-600 text-white"
        },
        blue: {
            bg: "bg-blue-50 dark:bg-blue-900/20",
            text: "text-blue-600 dark:text-blue-400",
            active: "bg-blue-600 text-white"
        }
    };

    const tabs = useMemo(() => Object.entries(servicesData), [servicesData]);
    const currentService = servicesData[activeTab] || tabs[0]?.[1];
    if (!currentService) return null;
    const currentColor = colorClasses[currentService.color] || colorClasses.teal;

    return (
        <section 
            className="py-16 lg:py-24 relative overflow-hidden" 
            id="services"
            style={{
                backgroundColor: '#479490',
                backgroundImage: 'url("https://www.transparenttextures.com/patterns/brushed-alum.png")'
            }}
        >
            <div className="absolute inset-0 bg-slate-900/20"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-teal-900/10 to-emerald-800/5"></div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full border border-white/20 mb-6">
                        <div className="w-2 h-2 bg-teal-300 rounded-full animate-pulse"></div>
                        <span className="renerg-kicker text-teal-800">OUR SERVICES</span>
                    </div>
                    <h2 className="renerg-section text-white mb-4">
                        <Link to="/services" className="hover:text-teal-100">{intro?.title || "Sustainable Energy Solutions"}</Link>
                    </h2>
                    <div className="renerg-body text-teal-100">
                        {intro?.body ? (
                          <HtmlContent html={intro.body} className="text-teal-100" imageTo="/#services" />
                        ) : (
                          <p>Comprehensive clean energy solutions for Rwanda and beyond</p>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap justify-center gap-3 mb-12">
                    {tabs.map(([key, service]) => (
                        <button
                            key={key}
                            onClick={() => setActiveTab(key)}
                            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 backdrop-blur-sm border ${
                                activeTab === key 
                                    ? (colorClasses[service.color] || colorClasses.teal).active + ' shadow-lg shadow-white/20'
                                    : "bg-white/90 text-slate-700 border-white/30 hover:bg-white hover:border-white/50"
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-lg">{service.icon}</span>
                                <span className="text-sm">{service.title.split(' - ')[0]}</span>
                            </div>
                        </button>
                    ))}
                </div>

                <div className="max-w-4xl mx-auto text-center mb-12">
                    <Link to={`/services/category/${activeTab}`} className="block bg-white/90 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-lg text-slate-700 text-lg leading-relaxed hover:shadow-xl">
                        <HtmlContent html={currentService.description} imageTo={`/services/category/${activeTab}`} />
                        <span className="mt-4 inline-block text-sm font-semibold text-teal-700">Open the full {currentService.title} category →</span>
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                    {currentService.services.map((item, index) => {
                      const to = item.id ? `/services/${item.id}` : "/#services";
                      return (
                            <Link key={item.key || index} to={to} className="group relative block">
                            <div className={`bg-white/95 backdrop-blur-sm rounded-2xl border border-white/30 p-6 hover:shadow-2xl hover:border-white/50 transition-all duration-300 group-hover:translate-y-[-4px] h-full flex flex-col shadow-lg`}>
                                    {item.image ? (
                                        <img src={item.image} alt="" className="mb-4 h-36 w-full rounded-xl object-cover" />
                                    ) : (
                                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${currentColor.bg} ${currentColor.text} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                                        {typeof item.icon === "string" ? (
                                          <span className="text-xl">{item.icon}</span>
                                        ) : item.icon ? (
                                          <item.icon className="text-xl" />
                                        ) : (
                                          <span className="text-xl">☀️</span>
                                        )}
                                    </div>
                                    )}

                                    <h3 className="renerg-h3 text-slate-900 mb-3">
                                        {item.title}
                                    </h3>
                                    <div className="renerg-body text-slate-600 mb-4 flex-grow text-left line-clamp-5">
                                      <HtmlContent html={item.desc} />
                                    </div>
                                    {item.id ? <span className="text-teal-700 font-semibold text-sm">Open this service →</span> : null}
                                </div>
                            </Link>
                      );
                    })}
                </div>

                <div className="text-center">
                  <Link to="/services" className="inline-flex rounded-xl bg-white px-6 py-3 font-semibold text-teal-800 shadow-lg">See all services →</Link>
                </div>
            </div>
        </section>
    );
}
